import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { classTypes } from 'src/lib/constants'
import { IModifiedPlan } from 'src/types/modifiedPlan.interface'
import {
  AdditionalFacilities,
  AdditionalRooms,
  Garages,
  History,
  PlanArea,
  PlanCreationDetails,
  PlanOverview,
  PlanPrice,
  PlanStructure,
  Specifications,
  StructureSpecification,
} from 'src/types/plan.interface'
import { ReleaseNotes } from 'src/types/releaseNotes.interface'

import { MODAL_STORE_KEY } from '../../constants/modal.constants'
import { Modal, ModalTypes } from '../../types/modal.interfaces'

const defaultPlanType = {
  additionalFacilities: {} as AdditionalFacilities,
  additionalRooms: {} as AdditionalRooms,
  communityPlanId: '',
  createdByCorporate: false,
  division: '',
  garages: {} as Garages,
  history: {} as History,
  id: '',
  isCeilingHeightsRequired: false,
  plan: '',
  planArea: {} as PlanArea,
  planCreatedBy: '',
  planCreatedDateTime: '',
  planCreationDetails: {} as PlanCreationDetails,
  planElevation: '',
  planId: '',
  planModifiedBy: '',
  planOverview: {} as PlanOverview,
  planPrice: {} as PlanPrice,
  planStructure: {} as PlanStructure,
  region: '',
  specifications: {} as Specifications,
  structureSpecification: {} as StructureSpecification,
  submitted: false,
  supportingDocuments: [],
} as unknown as IModifiedPlan

const modalInitialState: Modal = {
  approvalHistoryReportModalState: {
    isOpen: false,
  },
  bulkCommunityCopyModalState: {
    isOpen: false,
    selectedPlanIds: [''],
  },
  bulkEditModalState: {
    isOpen: false,
    selectedPlanIds: [''],
  },
  compareModalState: {
    isBulkExport: false,
    isOpen: false,
    selectedPlanIds: [''],
  },
  deleteModalState: {
    familyType: '',
    isOpen: false,
    planClass: '',
    planName: '',
    selectedPlanId: '',
  },
  floorPlanUploadModalState: {
    isOpen: false,
  },
  isOpen: false,
  markAsCoreModalState: {
    isOpen: false,
    planClass: classTypes.SINGLE,
    planIds: [''],
    planName: '',
    unset: false,
  },
  newVersionModalState: {
    isOpen: false,
  },
  planModalState: {
    copyPlanId: '',
    familyType: '',
    isOpen: false,
    planUse: '',
    selectedPlan: defaultPlanType,
  },
  printModalState: {
    isOpen: false,
    planName: '',
    printData: defaultPlanType,
  },
  propagateFilesModalState: {
    isOpen: false,
    selectedPlanIds: [''],
  },
  propagateModalState: {
    isOpen: false,
    selectedPlanIds: [''],
  },
  releaseNotesModalState: {
    isOpen: false,
    releaseNotesData: {} as ReleaseNotes,
  },
  selectedModal: '' as ModalTypes,
  shareModalState: {
    individualId: false,
    isOpen: false,
    selectedPlanIds: [''],
  },
  versionHistoryModalState: {
    isOpen: false,
    planId: '',
  },
}

export interface ResetModalPayload {
  property: keyof Omit<Modal, 'isOpen' | 'selectedModal'>
}

export const modalSlice = createSlice({
  initialState: modalInitialState,
  name: 'modalState',
  reducers: {
    addOrUpdateModalState: (state, action: PayloadAction<Partial<Modal>>) => {
      Object.assign(state, action.payload)
    },
    resetModal: (state, action: PayloadAction<ResetModalPayload>) => {
      const modalStateKey = action.payload.property

      switch (modalStateKey) {
        case 'approvalHistoryReportModalState':
          state.approvalHistoryReportModalState = {
            isOpen: false,
          }
          break
        case 'bulkCommunityCopyModalState':
          state.bulkCommunityCopyModalState = {
            isOpen: false,
            selectedPlanIds: [],
          }
          break
        case 'bulkEditModalState':
          state.bulkEditModalState = {
            isOpen: false,
            selectedPlanIds: [],
          }
          break
        case 'compareModalState':
          state.compareModalState = {
            isBulkExport: false,
            isOpen: false,
            selectedPlanIds: [],
          }
          break
        case 'deleteModalState':
          state.deleteModalState = {
            familyType: '',
            isOpen: false,
            planClass: '',
            planName: '',
            selectedPlanId: '',
          }
          break
        case 'planModalState':
          state.planModalState = {
            copyPlanId: '',
            familyType: '',
            isOpen: false,
            planUse: '',
            selectedPlan: defaultPlanType,
          }
          break
        case 'printModalState':
          state.printModalState = {
            isOpen: false,
            planName: '',
            printData: defaultPlanType,
          }
          break
        case 'propagateModalState':
          state.propagateModalState = {
            isOpen: false,
            selectedPlanIds: [''],
          }
          break
        case 'propagateFilesModalState':
          state.propagateModalState = {
            isOpen: false,
            selectedPlanIds: [''],
          }
          break
        case 'shareModalState':
          state.shareModalState = {
            individualId: false,
            isOpen: false,
            selectedPlanIds: [],
          }
          break
        case 'releaseNotesModalState':
          state.releaseNotesModalState = {
            isOpen: false,
            releaseNotesData: {} as ReleaseNotes,
          }
          break
        case 'versionHistoryModalState':
          state.versionHistoryModalState = { isOpen: false, planId: '' }
          break
        case 'markAsCoreModalState':
          state.markAsCoreModalState = modalInitialState.markAsCoreModalState
          break
        case 'newVersionModalState':
          state.newVersionModalState = {
            isOpen: false,
          }
          break
        default:
          break
      }

      state.isOpen = false
    },

    setModal: (
      state,
      action: PayloadAction<{
        property: ModalTypes
        value: Partial<Modal[keyof Modal]>
      }>
    ) => {
      state.selectedModal = action.payload.property
      const modalKey = MODAL_STORE_KEY[action.payload.property]
      const value = action.payload.value || {}
      Object.assign(state[modalKey], value)
      state.isOpen = true
    },
  },
})

export const { addOrUpdateModalState, resetModal, setModal } =
  modalSlice.actions
